// "Run Brand Intelligence" for a brand card: the modal sibling of the PDP
// and CV pickers. Shows the product pages the report will be built from
// (it uses the brand's active PDP URLs, up to five), a Run button, and the
// same "add a product" state as the pickers when there are none. Owns all
// feedback itself — no toasts — so a result never vanishes in four seconds.
import React, { useEffect, useState } from 'react';
import axiosInstant from '../../api';
import { rmnBrandReportTargetsEndpoint } from '../../api/endpoints';
import { RmnBrandReportTarget } from '../../types/rmn';
import { runBrandIntelligence, RunBrandIntelligenceResult } from './prepPackActions';
import { BORDER, FONT, GOLD, GOLD_DARK, TEXT_FAINT } from '../ProposalFlow/theme';

const ERROR = 'var(--down)';
const MAX_PAGES = 5;

interface Props {
  brandId: number;
  brandName: string;
  // POST that starts the pack (admin or rmn endpoint).
  runEndpoint: string;
  // Internal callers act as the brand's own root on the pages lookup.
  rmnBrandId?: number;
  onStarted: () => void;
  emptyAction?: { label: string; onClick: () => void };
}

const OUTCOME_MESSAGES: Record<Exclude<RunBrandIntelligenceResult, 'started' | 'no_product_urls'>, string> = {
  already_generating: 'A Sales Intel Prep Pack is already generating for this brand — it lands on the card when it finishes.',
  rate_limited: "You've reached the limit for Sales Intel Prep Packs. Try again later.",
  failed: "Couldn't start the Sales Intel Prep Pack. Please try again.",
};

export function BrandIntelligenceRunForm({ brandId, brandName, runEndpoint, rmnBrandId, onStarted, emptyAction }: Props) {
  const [pages, setPages] = useState<RmnBrandReportTarget[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const url = rmnBrandId
      ? `${rmnBrandReportTargetsEndpoint(brandId)}?rmn_brand_id=${rmnBrandId}`
      : rmnBrandReportTargetsEndpoint(brandId);
    axiosInstant
      .get(url)
      .then((res) => {
        if (!cancelled) setPages((res.data || []) as RmnBrandReportTarget[]);
      })
      .catch(() => {
        if (!cancelled) setLoadError("Couldn't load this brand's product pages. Please try again.");
      });
    return () => {
      cancelled = true;
    };
  }, [brandId, rmnBrandId]);

  const run = async () => {
    if (submitting) return;
    setSubmitting(true);
    setFormError(null);
    const outcome = await runBrandIntelligence(runEndpoint, brandName, { silent: true });
    setSubmitting(false);
    if (outcome === 'started') {
      onStarted();
      return;
    }
    if (outcome === 'no_product_urls') {
      setPages([]);
      return;
    }
    setFormError(OUTCOME_MESSAGES[outcome]);
  };

  if (loadError) return <div style={{ fontFamily: FONT, fontSize: 14, color: ERROR }}>{loadError}</div>;
  if (pages === null) {
    return <div style={{ fontFamily: FONT, fontSize: 14, color: TEXT_FAINT }}>Loading {brandName}&apos;s product pages…</div>;
  }

  if (pages.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <div style={{ fontFamily: FONT, fontSize: 14, color: TEXT_FAINT, lineHeight: 1.6 }}>
          {brandName} has no products with a product page, so there is nothing to build the report from. Add a
          product with its PDP URL first, then run the Sales Intel Prep Pack.
        </div>
        {emptyAction && (
          <button
            type="button"
            onClick={emptyAction.onClick}
            className="self-start px-[18px] py-[9px] rounded-[9px] text-[13px] font-semibold text-white bg-accent hover:bg-[#967435] transition-colors"
          >
            {emptyAction.label}
          </button>
        )}
      </div>
    );
  }

  const used = pages.slice(0, MAX_PAGES);
  const extra = pages.length - used.length;

  return (
    <div>
      <div style={{ fontFamily: FONT, fontSize: 13, color: TEXT_FAINT, lineHeight: 1.6, marginBottom: 12 }}>
        We&apos;ll audit these product pages, research the brand and its market, and assemble the Brand
        Intelligence report. It runs in the background and takes 10–20 minutes.
      </div>
      <div className="flex flex-col border border-border-token rounded-xl overflow-hidden mb-2 max-h-[280px] overflow-y-auto">
        {used.map((t) => (
          <div key={t.desireid} className="flex flex-col px-3 py-2.5 border-b border-border-token last:border-0">
            <span className="text-[13px] text-text-1 truncate" title={t.title || t.url}>
              {t.title || t.url}
            </span>
            <span className="text-[11px] text-muted truncate" title={t.url}>
              {t.url.replace(/^https?:\/\/(www\.)?/, '')}
            </span>
          </div>
        ))}
      </div>
      {extra > 0 && (
        <div className="text-[12px] text-muted mb-4">
          {extra} more {extra === 1 ? 'page is' : 'pages are'} on file; the report uses the first {MAX_PAGES}.
        </div>
      )}
      {formError && <div style={{ fontFamily: FONT, fontSize: 13, color: ERROR, margin: '12px 0 16px' }}>{formError}</div>}
      <button
        type="button"
        onClick={run}
        disabled={submitting}
        style={{
          width: '100%',
          padding: 14,
          marginTop: 12,
          borderRadius: 12,
          border: 'none',
          background: submitting ? BORDER : GOLD,
          color: submitting ? '#B5AFA5' : '#fff',
          fontFamily: FONT,
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
          cursor: submitting ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          if (!submitting) (e.currentTarget as HTMLButtonElement).style.background = GOLD_DARK;
        }}
        onMouseLeave={(e) => {
          if (!submitting) (e.currentTarget as HTMLButtonElement).style.background = GOLD;
        }}
      >
        {submitting ? 'Starting…' : 'Run Prep Pack'}
      </button>
    </div>
  );
}
